import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Camera, Trash2, X } from 'lucide-react';
import { journalApi, plantsApi, zonesApi } from '../services/gardenApi';
import { convertHeicFiles } from '../../utils/convertHeic';
import { useGardenPageTitle } from '../useGardenPageTitle';

const MAX_PHOTOS_PER_ENTRY = 8;

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Normalize an ISO timestamp to a YYYY-MM-DD string for <input type="date">.
function toInputDate(value) {
  if (!value) return todayISO();
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return todayISO();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function JournalEntryFormScreen() {
  const navigate = useNavigate();
  const { entryId } = useParams();
  const [search] = useSearchParams();
  const isEdit = Boolean(entryId);
  useGardenPageTitle(isEdit ? 'Edit entry' : 'New entry', isEdit ? 'Edit entry' : 'New entry');

  const [entry, setEntry] = useState(null);
  const [plants, setPlants] = useState([]);
  const [zones, setZones] = useState([]);
  const [form, setForm] = useState({
    body_text: '',
    plant_id: search.get('plant_id') || '',
    zone_id:  search.get('zone_id') || '',
    entry_date: todayISO(),
  });
  const [newPhotos, setNewPhotos] = useState([]); // File[] (unsaved)
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Preview URLs for newly picked files — revoked on unmount/change.
  const previews = useMemo(
    () => newPhotos.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [newPhotos]
  );
  useEffect(() => {
    return () => { for (const p of previews) URL.revokeObjectURL(p.url); };
  }, [previews]);

  const load = useCallback(async () => {
    try {
      const [p, z] = await Promise.all([
        plantsApi.list().catch(() => ({ plants: [] })),
        zonesApi.list().catch(() => ({ zones: [] })),
      ]);
      setPlants(p.plants || []);
      setZones(z.zones || []);

      if (isEdit) {
        const r = await journalApi.get(entryId);
        const e = r.entry;
        setEntry(e);
        setForm({
          body_text: e.body_text || '',
          plant_id: e.garden_plant_id || '',
          zone_id:  e.zone_id || '',
          entry_date: toInputDate(e.entry_date),
        });
      }
    } catch (e) {
      setError(e.message || 'Failed to load entry');
    } finally {
      setLoading(false);
    }
  }, [entryId, isEdit]);

  useEffect(() => { load(); }, [load]);

  const existingPhotos = entry?.photo_urls || [];
  const totalPhotos = existingPhotos.length + newPhotos.length;

  const onPickFiles = async (e) => {
    let files = Array.from(e.target.files || []);
    try {
      files = await convertHeicFiles(files);
    } catch (err) {
      setError(err.message || 'Could not convert HEIC image. Please try a JPG or PNG.');
      e.target.value = '';
      return;
    }
    const room = MAX_PHOTOS_PER_ENTRY - totalPhotos;
    if (files.length > room) {
      setError(`You can attach up to ${MAX_PHOTOS_PER_ENTRY} photos per entry.`);
      setNewPhotos((prev) => [...prev, ...files.slice(0, room)]);
    } else {
      setError(null);
      setNewPhotos((prev) => [...prev, ...files]);
    }
    e.target.value = '';
  };

  const removeNewPhoto = (file) => {
    setNewPhotos((prev) => prev.filter((f) => f !== file));
  };

  const removeExistingPhoto = async (url) => {
    if (!entry) return;
    if (!window.confirm('Remove this photo from the entry?')) return;
    try {
      const r = await journalApi.removePhoto(entry.id, url);
      setEntry(r.entry);
    } catch (e) {
      setError(e.message || 'Could not remove photo');
    }
  };

  const canSave =
    !saving &&
    (form.body_text.trim().length > 0 ||
      existingPhotos.length > 0 ||
      newPhotos.length > 0);

  const handleSave = async () => {
    if (!canSave) {
      setError('Add a note or at least one photo.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await journalApi.patch(entry.id, {
          body_text: form.body_text,
          plant_id:  form.plant_id || null,
          zone_id:   form.zone_id  || null,
          entry_date: form.entry_date,
        });
        if (newPhotos.length > 0) {
          await journalApi.addPhotos(entry.id, newPhotos);
        }
      } else {
        await journalApi.create({
          body_text:  form.body_text,
          plant_id:   form.plant_id || null,
          zone_id:    form.zone_id  || null,
          entry_date: form.entry_date,
          photos:     newPhotos,
        });
      }
      navigate('/garden/journal');
    } catch (e) {
      setError(e.message || 'Save failed');
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!entry) return;
    if (!window.confirm('Delete this entry permanently?')) return;
    setDeleting(true);
    try {
      await journalApi.remove(entry.id);
      navigate('/garden/journal');
    } catch (e) {
      setError(e.message || 'Delete failed');
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="px-4 py-6 text-sm text-garden-muted">Loading…</div>;
  }

  return (
    <div className="relative pb-36">
      <header className="flex items-center gap-2 px-2 pt-2 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-ink"
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>
        <h1 className="text-lg font-semibold text-garden-ink">
          {isEdit ? 'Edit entry' : 'New entry'}
        </h1>
      </header>

      {error ? (
        <div className="mx-4 mb-3 p-3 rounded-xl bg-red-50 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="px-4 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-garden-muted mb-1">Date</label>
          <input
            type="date"
            value={form.entry_date}
            onChange={(e) => setForm((f) => ({ ...f, entry_date: e.target.value }))}
            className="w-full min-h-11 px-3 rounded-xl bg-white border border-garden-ink/10 text-sm focus:outline-none focus:border-garden-primary"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-garden-muted mb-1">Note</label>
          <textarea
            rows={5}
            value={form.body_text}
            onChange={(e) => setForm((f) => ({ ...f, body_text: e.target.value }))}
            placeholder="What happened in the garden today?"
            className="w-full px-3 py-2 rounded-xl bg-white border border-garden-ink/10 text-sm focus:outline-none focus:border-garden-primary"
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-garden-muted mb-1">
              Plant (optional)
            </label>
            <select
              value={form.plant_id}
              onChange={(e) => setForm((f) => ({ ...f, plant_id: e.target.value }))}
              className="w-full min-h-11 px-3 rounded-xl bg-white border border-garden-ink/10 text-sm focus:outline-none focus:border-garden-primary"
            >
              <option value="">{'\u2014 None \u2014'}</option>
              {plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.common_name}{p.botanical_name ? ` (${p.botanical_name})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-garden-muted mb-1">
              Zone (optional)
            </label>
            <select
              value={form.zone_id}
              onChange={(e) => setForm((f) => ({ ...f, zone_id: e.target.value }))}
              className="w-full min-h-11 px-3 rounded-xl bg-white border border-garden-ink/10 text-sm focus:outline-none focus:border-garden-primary"
            >
              <option value="">{'\u2014 None \u2014'}</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>{z.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-semibold text-garden-muted">
              Photos ({totalPhotos}/{MAX_PHOTOS_PER_ENTRY})
            </label>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={totalPhotos >= MAX_PHOTOS_PER_ENTRY}
              className="inline-flex items-center gap-1.5 min-h-8 px-3 rounded-full bg-garden-primary/10 text-garden-primary text-xs font-medium disabled:opacity-50"
            >
              <Camera size={14} /> Add photos
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,image/heic,image/heif,.heic,.heif"
              multiple
              onChange={onPickFiles}
              className="hidden"
            />
          </div>

          {(existingPhotos.length + previews.length) === 0 ? (
            <div className="rounded-xl border border-dashed border-garden-ink/20 p-4 text-xs text-garden-muted text-center">
              No photos yet. Tap <b>Add photos</b> to attach some.
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {existingPhotos.map((url) => (
                <div key={url} className="relative aspect-square rounded-lg overflow-hidden bg-garden-surface">
                  <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  <button
                    type="button"
                    onClick={() => removeExistingPhoto(url)}
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center"
                    aria-label="Remove photo"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              {previews.map((p) => (
                <div key={p.url} className="relative aspect-square rounded-lg overflow-hidden bg-garden-surface ring-1 ring-garden-primary/40">
                  <img src={p.url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeNewPhoto(p.file)}
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center"
                    aria-label="Remove unsaved photo"
                  >
                    <X size={14} />
                  </button>
                  <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded-full bg-garden-primary text-white text-[10px] font-semibold">
                    New
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {isEdit ? (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
          >
            <Trash2 size={16} /> Delete entry
          </button>
        ) : null}
      </div>

      <div
        className="fixed bottom-16 left-0 right-0 z-20 bg-white/95 backdrop-blur border-t border-black/5 px-4 py-3"
      >
        <div className="mx-auto max-w-md flex gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 min-h-11 rounded-full bg-white border border-garden-ink/10 text-sm font-medium text-garden-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="flex-1 min-h-11 rounded-full bg-garden-primary text-white text-sm font-medium disabled:opacity-50 active:scale-95 transition"
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save entry'}
          </button>
        </div>
      </div>
    </div>
  );
}
